/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { instanceSymbol } from "../constants.mjs";

export { Base };

/**
 * This is the base class from which the most classes are derived.
 *
 * This class has besides a `toString` which returns the json representation of the object
 * also a functionality to check if an object is an instance of a class.
 *
 * Therefor the class has a static method ` [Symbol.hasInstance](that)` which returns true if the object
 * is an instance of the class.
 *
 * @see [https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Symbol/hasInstance](developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Symbol/hasInstance)
 *
 * Derived classes should implement a static getter `instanceSymbol` which returns a unique symbol.
 *
 * ```javascript
 * static get [instanceSymbol]() {
 *    return Symbol.for("@schukai/monster/types/base");
 * }
 * ```
 *
 * The class was formerly called Object.
 *
 * @license AGPLv3
 * @since 1.5.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
class Base extends Object {
	/**
	 *
	 * @returns {string}
	 */
	toString() {
		return JSON.stringify(this);
	}

	/**
	 * This method is called by the `instanceof` operator.
	 * @returns {symbol}
	 * @since 2.1.0
	 */
	static get [instanceSymbol]() {
		return Symbol.for("@schukai/monster/types/base");
	}

	/**
	 * This method is called by the `instanceof` operator.
	 * @param that
	 * @returns {boolean}
	 * @since 2.1.0
	 */
	static [Symbol.hasInstance](that) {
		if (
			that === undefined ||
			that === null ||
			(typeof that !== "object" && typeof that !== "function")
		) {
			return false;
		}

		const thatClass = Object.getPrototypeOf(that);
		if (
			thatClass === undefined ||
			thatClass === null ||
			(typeof thatClass !== "object" && typeof thatClass !== "function")
		) {
			return false;
		}

		if (checkInstanceSymbol.apply(this, [thatClass]) === true) {
			return true;
		}

		// this call the static method of the super class, if there is one
		return super[Symbol.hasInstance](that);
	}
}

/**
 * this function checks if the class has a static getter `instanceSymbol` and if the value of this getter is equal to the
 *
 * @private
 * @param obj
 * @returns {boolean|any|boolean}
 * @since 2.1.0
 */
function checkInstanceSymbol(obj) {
	if (this.hasOwnProperty(instanceSymbol) === false) {
		return false;
	}

	const proto = obj?.constructor;
	if (
		proto === undefined ||
		proto === null ||
		(typeof proto !== "object" && typeof proto !== "function")
	) {
		return false;
	}

	if (proto.hasOwnProperty(instanceSymbol) !== true) {
		return checkInstanceSymbol.apply(this, [obj.__proto__]);
	}

	const symbol = proto[instanceSymbol];
	if (symbol === undefined) {
		if (obj.__proto__) {
			return checkInstanceSymbol(obj.__proto__);
		} else {
			return false;
		}
	}

	if (symbol === this[instanceSymbol]) {
		return true;
	}

	return checkInstanceSymbol.apply(this, [obj.__proto__]);
}
